// Taxonomy API - Public and Admin endpoints
import { request } from "./base";

// Taxonomy Types
export type BusinessType = {
	id: string;
	name: string;
	description: string | null;
	icon: string | null;
	sortOrder: number;
	isActive: boolean;
	dateCreated: string;
};

export type TimelineCategory = {
	id: string;
	name: string;
	description: string | null;
	sortOrder: number;
	isActive: boolean;
	dateCreated: string;
};

export type CustomerSegment = {
	id: string;
	name: string;
	description: string | null;
	priceIndicator: string | null;
	sortOrder: number;
	isActive: boolean;
	dateCreated: string;
};

export type ProjectScale = {
	id: string;
	name: string;
	description: string | null;
	sortOrder: number;
	isActive: boolean;
	dateCreated: string;
};

export type ServiceCategory = {
	id: string;
	name: string;
	parentId: string | null;
	icon: string | null;
	sortOrder: number;
	isActive: boolean;
	dateCreated: string;
	children?: ServiceCategory[];
};

export type MaterialTag = {
	id: string;
	name: string;
	category: "core" | "finish" | "hardware";
	sortOrder: number;
	isActive: boolean;
	dateCreated: string;
};

export type Brand = {
	id: string;
	name: string;
	logoUrl: string | null;
	category:
		| "hardware"
		| "laminates"
		| "plywood_boards"
		| "kitchen_appliances"
		| "modular_systems"
		| "paints"
		| "sanitaryware";
	websiteUrl: string | null;
	sortOrder: number;
	isActive: boolean;
	dateCreated: string;
};

export type City = {
	id: string;
	name: string;
	state: string;
	sortOrder: number;
	isActive: boolean;
	dateCreated: string;
};

export type Zone = {
	id: string;
	cityId: string;
	name: string;
	sortOrder: number;
	isActive: boolean;
	dateCreated: string;
};

export type Locality = {
	id: string;
	zoneId: string;
	name: string;
	pinCodes: string[] | null;
	sortOrder: number;
	isActive: boolean;
	dateCreated: string;
};

export type RoomType = {
	code: string;
	displayName: string;
	displayNameHindi: string | null;
	icon: string | null;
	sortOrder: number;
	isActive: boolean;
	dateCreated: string;
};

// Generic taxonomy item type (union of all taxonomy types)
export type TaxonomyItem =
	| BusinessType
	| TimelineCategory
	| CustomerSegment
	| ProjectScale
	| ServiceCategory
	| MaterialTag
	| Brand
	| City
	| Zone
	| Locality;

// Public Taxonomy API (no auth required)
export const taxonomyApi = {
	async getBusinessTypes() {
		return request<BusinessType[]>(`/api/taxonomy/business-types`);
	},

	async getTimelineCategories() {
		return request<TimelineCategory[]>(`/api/taxonomy/timeline-categories`);
	},

	// Alias for onboarding steps that use "capabilities" terminology
	async getTimelineCapabilities() {
		return request<TimelineCategory[]>(`/api/taxonomy/timeline-categories`);
	},

	async getCustomerSegments() {
		return request<CustomerSegment[]>(`/api/taxonomy/customer-segments`);
	},

	async getProjectScales() {
		return request<ProjectScale[]>(`/api/taxonomy/project-scales`);
	},

	async getServiceCategories() {
		return request<ServiceCategory[]>(`/api/taxonomy/service-categories`);
	},

	async getMaterialTags() {
		return request<Record<string, MaterialTag[]>>(
			`/api/taxonomy/material-tags`,
		);
	},

	async getBrands() {
		return request<Record<string, Brand[]>>(`/api/taxonomy/brands`);
	},

	async getCities() {
		return request<City[]>(`/api/taxonomy/cities`);
	},

	async getZones(cityId: string) {
		return request<Zone[]>(`/api/taxonomy/cities/${cityId}/zones`);
	},

	async getLocalities(zoneId: string) {
		return request<Locality[]>(`/api/taxonomy/zones/${zoneId}/localities`);
	},

	async getLocalitiesByCity(cityId: string) {
		return request<Locality[]>(`/api/taxonomy/cities/${cityId}/localities`);
	},

	async getRoomTypes() {
		return request<RoomType[]>(`/api/taxonomy/room-types`);
	},
};
